import {
  Button,
  CheckIcon,
  ChevronLeftIcon,
  DismissIcon,
  Flex,
  Typography,
} from '@iheartjane/reefer';
import { useCallback, useEffect, useState } from 'react';

import { useResetPasswordRequestFetcher } from '@jane/bloom/hooks/useResetPasswordRequestFetcher';
import type { CheckoutAuthFormStep } from '@jane/bloom/routes/$basename.checkout._index/types';

import styles from './forgotPasswordModalContent.module.css';

export interface ForgotPasswordProps {
  onClose: () => void;
  setFormStep?: (formStep: CheckoutAuthFormStep) => void;
  userEmail: string;
}

export const ForgotPasswordModalContent = ({
  onClose,
  setFormStep,
  userEmail,
}: ForgotPasswordProps) => {
  const { sendResetPasswordRequest, state } = useResetPasswordRequestFetcher();
  const [formSubmitted, setFormSubmitted] = useState(false);

  const submit = useCallback(() => {
    if (formSubmitted || state !== 'idle') {
      return;
    }
    sendResetPasswordRequest(userEmail);
    setFormSubmitted(true);
  }, [formSubmitted, sendResetPasswordRequest, state, userEmail]);

  useEffect(() => {
    if (state === 'idle' && formSubmitted) {
      if (setFormStep) {
        setFormStep('existingUserAfterReset');
      } else {
        onClose();
      }
    }
  }, [state, formSubmitted, setFormStep, onClose]);

  return (
    <Flex flexDirection="column" alignItems="center">
      <Flex
        flexDirection="row"
        alignItems="center"
        justifyContent="space-between"
        width="100%"
      >
        {setFormStep && (
          <Button.Icon
            className={styles['arrowButton']}
            icon={<ChevronLeftIcon />}
            label="go back"
            onClick={() => setFormStep('existingUser')}
          />
        )}
        <Button.Icon
          className={styles['closeButton']}
          icon={<DismissIcon />}
          label="close"
          onClick={onClose}
        />
      </Flex>
      <Flex flexDirection="column" alignItems="center" px={24} pb={24}>
        <Typography variant="header" textAlign="center" mb={24}>
          Reset Password
        </Typography>
        <Typography
          color="grays-mid"
          textAlign="center"
          className={styles['resetPswInstructions']}
        >
          An email with a link to reset your password will be sent to this email
          address.
        </Typography>
        <Typography
          variant="body-bold"
          textAlign="center"
          className={styles['forgotPassEmail']}
        >
          {userEmail}
        </Typography>
        <Button
          ariaLabel="Send reset password email"
          onClick={submit}
          variant="primary"
          label={
            state === 'loading' ? (
              <div>
                <CheckIcon color="text-inverse" />
              </div>
            ) : (
              'Send email'
            )
          }
          className={styles['sendButton']}
        />
      </Flex>
    </Flex>
  );
};
